/**
 * AccessDeniedScreen — ekran „brak uprawnień" w dwóch wariantach.
 *
 * Wariant zależy od tego, JAK użytkownik trafił na zablokowaną trasę (React Router `useNavigationType`):
 *  • PUSH/REPLACE (klik w link wewnątrz aplikacji) → okno MODAL nad interfejsem; zamknięcie wraca
 *    tam, skąd przyszedł, więc kliknięcie w niedostępny element nie wyrzuca z kontekstu pracy,
 *  • POP (wejście z adresu, odświeżenie, zakładka przeglądarki) → pełnoekranowa karta, bo nie ma
 *    dokąd wracać.
 *
 * [G1 2026-08-31] PRZEBUDOWA UI. Było: badge, tytuł i sucha tabelka Moduł/Powód/Adres — ekran mówił
 * WYŁĄCZNIE „nie wolno", nie mówiąc ani czego brakuje, ani kto to nadaje, ani co zrobić teraz.
 * Jest: ten sam komplet danych opakowany w trzy KROKI i jedno zdanie o tym, kto nadaje dostęp.
 *
 * [BRIEF 2026-09-01 A] ZGŁOSZENIE Z UZASADNIENIEM. Było: „Wyślij zgłoszenie do IT" jednym kliknięciem
 * tworzyło zgłoszenie z adresem i kluczem uprawnienia, ale bez ani słowa o tym, PO CO komuś ten
 * dostęp — IT dostawało polecenie bez powodu i musiało dopytywać. Właściciel: „pisząc zgłoszenie do
 * IT, mam napisać uzasadnienie po co mi tam dostęp". Jest: przycisk otwiera SZUFLADĘ PO PRAWEJ
 * z wypełnionymi automatycznie danymi (kto prosi, moduł, adres, klucz PERM z bramki trasy) i jednym
 * polem do wpisania — uzasadnieniem (wymagane, min. MIN_JUSTIFICATION znaków, tyle samo co na
 * serwerze). Zgłoszenie idzie na osobne wejście `/my-tickets/access-request`, na którym serwer
 * ODMAWIA bez uzasadnienia — formularz w przeglądarce nie jest jedyną bramką.
 *
 * [KANON OKIEN 2026-09-07] „Każde okno wygląda inaczej niż aplikacja." Na tym ekranie były DWA okna
 * naraz — karta odmowy i szuflada „Prośba o dostęp" — i szuflada miała INDYGO ZASZYTE NA SZTYWNO,
 * więc przy innej palecie karta zmieniała kolor, a szuflada nie: jedno okno, dwa akcenty. Cały arkusz
 * (poza głową karty, której kolory dokłada jawnie styles/accent.css) stoi teraz na tokenach, stopki
 * i przyciski zamknięcia poszły na kanon, a motyw ciemny ma własne nadpisania. Decyzje zamraża
 * zapadka `src/components/__tests__/AccessDeniedScreen.test.js` (blok „KANON OKIEN").
 *
 * Konwencje repo: modal i szuflada zamykają się wyłącznie przyciskami (klik w tło = potrząśnięcie /
 * szturchnięcie). Kolory na tokenach --color-* → motyw jasny i ciemny z jednego kodu.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useNavigate, useNavigationType, useLocation } from "react-router-dom";
import {
    FiLock, FiArrowLeft, FiHome, FiCopy, FiCheck, FiSend, FiX, FiLifeBuoy,
    FiRefreshCw,
} from "react-icons/fi";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import { apiService } from "../../api/apiService";
import { useAuth } from "../../hooks/useAuth";
import { shakeModal, nudgeDrawer } from "../../utils/modalAttention";
import s from "./AccessDeniedScreen.module.css";

/* [POLECENIE WŁAŚCICIELA 2026-09-08] BYŁO źLE na dwa sposoby naraz.

   (1) Ekran instruował pracownika, GDZIE nadaje się uprawnienia — „poproś administratora",
       a w wierszu „Powód" nawet „Panel administracyjny → Uprawnienia i dostęp". To nie jest
       sprawa pracownika: on nie ma tam wejścia i nie ma czego szukać. Wiedza o tym, który
       ekran nadaje klucz, należy do działu IT, który zgłoszenie obsłuży.

   (2) Ekran obiecywał wynik, którego nie ma: „poproś o nadanie" brzmi jak formalność.
       Wysłanie zgłoszenia NIE JEST równoznaczne z otrzymaniem dostępu — wniosek przechodzi
       WERYFIKACJĘ i może zostać odrzucony. Ekran ma to powiedzieć wprost, zanim ktoś
       zaplanuje sobie pracę na jutro w module, którego nie dostanie. */
const DEFAULT_MESSAGE =
    "Nie masz dostępu do tej sekcji. Jeśli potrzebujesz go do pracy, możesz wysłać zgłoszenie o dostęp — "
    + "zostanie zweryfikowane i rozpatrzone. O wyniku poinformujemy Cię w powiadomieniach i mailem.";

/** Lustro `AccessRequestTicketDto.MinJustificationLength` / `MaxJustificationLength` z backendu —
 *  serwer odrzuca krótsze uzasadnienie (400), więc przycisk nie może obiecać wysyłki, której nie będzie. */
export const MIN_JUSTIFICATION = 20;
export const MAX_JUSTIFICATION = 2000;

const AccessDeniedScreen = ({
    title = "Brak uprawnień",
    message = DEFAULT_MESSAGE,
    moduleLabel,
    reason,
    permissionKey,
    /**
     * [polecenie właściciela 2026-09-06, pkt 1] „Albo wniosek, albo do widzenia."
     *
     * Nie na każdym z tych ekranów jest o co wnioskować. Gdy moduł jest WYŁĄCZONY W CAŁEJ
     * APLIKACJI, dział IT nie ma czego nadać — nie brakuje żadnego uprawnienia, tylko administrator
     * zamknął moduł wszystkim. Oferowanie wtedy „Wyślij zgłoszenie do IT" produkuje zgłoszenia,
     * których nie da się załatwić, i obiecuje człowiekowi dostęp, którego nikt mu nie otworzy.
     */
    mozliwyWniosek = true,
}) => {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const location = useLocation();
    const { user, refreshSession } = useAuth();

    const [copied, setCopied] = useState(false);
    const [sending, setSending] = useState(false);
    const [sentNumber, setSentNumber] = useState(null);
    const [sendError, setSendError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshedNoChange, setRefreshedNoChange] = useState(false);

    // Szuflada „Prośba o dostęp" — uzasadnienie wpisuje użytkownik, resztę wypełniamy sami.
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [justification, setJustification] = useState("");
    const [touched, setTouched] = useState(false);
    const textareaRef = useRef(null);

    // Wejście z paska adresu / odświeżenie strony = brak historii w aplikacji → pełny ekran.
    const asModal = navigationType !== "POP";

    // Treść do schowka — jedno źródło, żeby przełożony/IT dostał dokładnie to, co widzi user.
    const report = useMemo(() => {
        const lines = [
            `Brak uprawnień: ${moduleLabel || "sekcja aplikacji"}`,
            "",
            `Adres: ${window.location.origin}${location.pathname}`,
        ];
        if (permissionKey) lines.push(`Wymagane uprawnienie: ${permissionKey}`);
        if (reason) lines.push(`Powód: ${reason}`);
        if (user?.email) lines.push(`Konto: ${user.email}`);
        lines.push(`Data: ${new Date().toLocaleString("pl-PL")}`);
        return lines.join("\n");
    }, [moduleLabel, permissionKey, reason, location.pathname, user]);

    /* [TESTY LOSOWE 2026-10-05] ZEGAR ETYKIETY „Skopiowano" MA JEDNEGO WŁAŚCICIELA.
       Było: każde kliknięcie nastawiało WŁASNY, niczyj zegar 2,2 s. Drugie kliknięcie po 2 s dostawało
       więc „Skopiowano" na 0,2 s — gasił je zegar pierwszego kliknięcia — a zegar żył dalej po zamknięciu
       ekranu (i po końcu testu, który go nastawił). Teraz nowe kliknięcie kasuje poprzedni zegar,
       a zdjęcie ekranu kasuje ostatni; kopiowanie dokończone już po zdjęciu ekranu niczego nie nastawia. */
    const copiedTimerRef = useRef(null);
    const zamontowanyRef = useRef(true);
    useEffect(() => {
        zamontowanyRef.current = true;
        return () => {
            zamontowanyRef.current = false;
            clearTimeout(copiedTimerRef.current);
        };
    }, []);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(report);
        } catch {
            // Starsze przeglądarki / brak zgody na schowek — awaryjnie przez ukryte pole.
            const area = document.createElement("textarea");
            area.value = report;
            area.style.position = "fixed";
            area.style.opacity = "0";
            document.body.appendChild(area);
            area.select();
            try { document.execCommand("copy"); } catch { /* nic więcej nie zrobimy */ }
            document.body.removeChild(area);
        }
        if (!zamontowanyRef.current) return;
        setCopied(true);
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = setTimeout(() => setCopied(false), 2200);
    }, [report]);

    const trimmedLength = justification.trim().length;
    const justificationTooShort = trimmedLength < MIN_JUSTIFICATION;
    const justificationTooLong = justification.length > MAX_JUSTIFICATION;
    const canSubmit = !justificationTooShort && !justificationTooLong && !sending;

    const openDrawer = useCallback(() => {
        setSendError(null);
        setTouched(false);
        setDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        if (sending) return;
        setDrawerOpen(false);
    }, [sending]);

    useEffect(() => {
        if (drawerOpen) textareaRef.current?.focus();
    }, [drawerOpen]);

    const handleSubmitRequest = useCallback(async () => {
        setTouched(true);
        if (!canSubmit) return;
        setSending(true);
        setSendError(null);
        try {
            const res = await apiService.createAccessRequestTicket({
                justification: justification.trim(),
                path: location.pathname,
                permissionKey: permissionKey || null,
                moduleLabel: moduleLabel || null,
                reason: reason || null,
            });
            setSentNumber(res.data?.ticketNumber || "—");
            setDrawerOpen(false);
        } catch (err) {
            setSendError(
                err?.response?.data?.message ||
                "Nie udało się wysłać zgłoszenia. Skopiuj komunikat i wyślij go na adres działu IT."
            );
        } finally {
            setSending(false);
        }
    }, [canSubmit, justification, location.pathname, permissionKey, moduleLabel, reason]);

    /**
     * Uprawnienia siedzą w tokenie z chwili logowania. Gdy administrator nada dostęp „przed chwilą",
     * przeglądarka nadal odbija tym ekranem, dopóki użytkownik się nie przeloguje. Ten przycisk pobiera
     * aktualne uprawnienia z serwera — jeśli dostęp już jest, bramka trasy wpuści bez wylogowania.
     */
    /* [polecenie właściciela 2026-09-06, pkt 1] BYŁO ŹLE: `refreshedNoChange` ustawiało się
       BEZWARUNKOWO — także w gałęzi sukcesu i w catch. Komunikat „nadal nie ma tu dostępu"
       pokazywał się więc także wtedy, gdy dostęp właśnie przyszedł (przy widoczności modułu,
       która dociera osobnym żądaniem, migal na ułamek sekundy jako nieprawda).
       TERAZ: porównujemy zwrócone uprawnienia z tym, którego brakuje. Gdy klucza nie znamy
       (wariant „moduł wyłączony"), milczymy — ekran i tak zniknie sam, gdy blokada ustanie. */
    const handleRefreshPermissions = useCallback(async () => {
        setRefreshing(true);
        setRefreshedNoChange(false);
        try {
            // Gdy uprawnienie doszło, ProtectedRoute przerenderuje trasę i ten ekran zniknie sam.
            const swiezeUprawnienia = await refreshSession();
            const wymagane = (permissionKey || "").split(" lub ").map((k) => k.trim()).filter(Boolean);
            const dostal = wymagane.length > 0 && Array.isArray(swiezeUprawnienia)
                && wymagane.some((k) => swiezeUprawnienia.includes(k));
            setRefreshedNoChange(wymagane.length > 0 && !dostal);
        } catch {
            setRefreshedNoChange(false);
            setSendError("Nie udało się sprawdzić uprawnień na serwerze. Spróbuj jeszcze raz.");
        } finally {
            setRefreshing(false);
        }
    }, [refreshSession, permissionKey]);

    const goBack = useCallback(() => {
        if (asModal) navigate(-1);
        else navigate(localizedPath("dashboard"));
    }, [asModal, navigate]);

    const requesterLabel = useMemo(() => {
        const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
        if (name && user?.email) return `${name} (${user.email})`;
        return name || user?.email || "—";
    }, [user]);

    // Szuflada idzie przez portal: karta modalu ma transform (animacja wejścia) i backdrop-filter,
    // a każde z nich robi z niej „containing block" — position:fixed w środku kleiłoby się do karty,
    // nie do okna przeglądarki.
    const drawer = drawerOpen ? createPortal(
        <div className={s.drawerOverlay} onClick={nudgeDrawer} data-testid="access-request-overlay">
            <aside
                className={s.drawer}
                role="dialog"
                aria-modal="true"
                aria-labelledby="access-request-title"
            >
                <div className={s.drawerHead}>
                    <div className={s.drawerHeadTexts}>
                        <span className={s.drawerKicker}>Zgłoszenie do IT</span>
                        <h2 className={s.drawerTitle} id="access-request-title">Prośba o dostęp</h2>
                        <p className={s.drawerSubtitle}>
                            Dane poniżej wypełniliśmy za Ciebie. Dopisz tylko, po co potrzebujesz tego dostępu —
                            bez uzasadnienia IT nie może nadać uprawnienia.
                        </p>
                    </div>
                    {/* Kanon okien: zamknięcie = przycisk ikonowy 36 × 36 (rozmiar w .drawerCloseBtn). */}
                    <button
                        type="button"
                        className={s.drawerCloseBtn}
                        onClick={closeDrawer}
                        aria-label="Zamknij szufladę"
                        disabled={sending}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div className={s.drawerBody}>
                    <div className={s.drawerSectionTitle}>Czego dotyczy prośba</div>
                    <div className={s.drawerFacts}>
                        <div className={s.drawerFact}>
                            <span className={s.drawerFactLabel}>Kto prosi</span>
                            <span className={s.drawerFactValue}>{requesterLabel}</span>
                        </div>
                        <div className={s.drawerFact}>
                            <span className={s.drawerFactLabel}>Moduł / strona</span>
                            <span className={s.drawerFactValue}>{moduleLabel || "sekcja aplikacji"}</span>
                        </div>
                        <div className={s.drawerFact}>
                            <span className={s.drawerFactLabel}>Adres w aplikacji</span>
                            <span className={`${s.drawerFactValue} ${s.detailMono}`}>{location.pathname}</span>
                        </div>
                        {permissionKey && (
                            <div className={s.drawerFact}>
                                <span className={s.drawerFactLabel}>Wymagane uprawnienie</span>
                                <span className={`${s.drawerFactValue} ${s.detailMono}`}>{permissionKey}</span>
                            </div>
                        )}
                        {reason && (
                            <div className={s.drawerFact}>
                                <span className={s.drawerFactLabel}>Powód odmowy</span>
                                <span className={s.drawerFactValue}>{reason}</span>
                            </div>
                        )}
                    </div>

                    <div className={s.drawerSectionTitle}>Uzasadnienie</div>
                    <label className={s.drawerLabel} htmlFor="access-request-justification">
                        Po co potrzebujesz tego dostępu? <span className={s.required}>*</span>
                    </label>
                    <textarea
                        id="access-request-justification"
                        ref={textareaRef}
                        className={`${s.drawerTextarea} ${touched && (justificationTooShort || justificationTooLong) ? s.drawerTextareaInvalid : ""}`}
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        onBlur={() => setTouched(true)}
                        rows={5}
                        maxLength={MAX_JUSTIFICATION + 200}
                        placeholder="Np. Rozliczam urlopy zespołu montażu i muszę widzieć grafik pracy działu, żeby zatwierdzać wnioski."
                        aria-required="true"
                        aria-invalid={touched && (justificationTooShort || justificationTooLong) ? "true" : "false"}
                        aria-describedby="access-request-justification-hint"
                    />
                    <div
                        id="access-request-justification-hint"
                        className={`${s.drawerHint} ${touched && (justificationTooShort || justificationTooLong) ? s.drawerHintInvalid : ""}`}
                    >
                        {justificationTooLong
                            ? `Uzasadnienie jest za długie — maksymalnie ${MAX_JUSTIFICATION} znaków.`
                            : justificationTooShort
                                ? `Napisz co najmniej ${MIN_JUSTIFICATION} znaków (masz ${trimmedLength}) — jedno słowo to nie uzasadnienie.`
                                : "Dziękujemy — to wystarczy, żeby IT mogło ocenić prośbę."}
                    </div>

                    {sendError && <div className={s.errorBox}>{sendError}</div>}
                </div>

                <div className={s.drawerFooter}>
                    <button type="button" className={s.ghostBtn} onClick={closeDrawer} disabled={sending}>
                        Anuluj
                    </button>
                    <button
                        type="button"
                        className={s.primaryBtn}
                        onClick={handleSubmitRequest}
                        disabled={!canSubmit}
                    >
                        <FiSend size={14} />
                        {sending ? "Wysyłam…" : "Wyślij zgłoszenie"}
                    </button>
                </div>
            </aside>
        </div>,
        document.body
    ) : null;

    const card = (
        <motion.div
            className={`${s.card} ${asModal ? s.cardModal : ""}`}
            initial={{ opacity: 0, y: asModal ? 8 : 12, scale: asModal ? 0.985 : 1 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            role="dialog"
            aria-modal={asModal ? "true" : undefined}
            aria-labelledby="access-denied-title"
        >
            {/* [polecenie właściciela 2026-09-06, pkt 1] Nagłówek idzie za paletą przez regułę dopisaną
                JAWNIE w styles/accent.css. Kusiło, żeby po prostu przemianować klasę na `hero` — ale ten
                ekran jest głową KARTY osadzanej w stronach, które mają własny nagłówek, i taka nazwa
                złamałaby zasadę „jeden nagłówek na stronie" (złapała to zapadka naglowkiOsadzonychStron). */}
            <div className={s.head}>
                <div className={s.headGlow} />
                <div className={s.headRow}>
                    <span className={s.iconWrap}><FiLock size={20} /></span>
                    <div className={s.headTexts}>
                        <span className={s.badge}>Dostęp ograniczony</span>
                        <h1 className={s.title} id="access-denied-title">{title}</h1>
                    </div>
                    {asModal && (
                        /* Kanon okien: zamknięcie = przycisk ikonowy 36 × 36 (rozmiar w .closeBtn). */
                        <button type="button" className={s.closeBtn} onClick={goBack} aria-label="Zamknij">
                            <FiX size={18} />
                        </button>
                    )}
                </div>
                <p className={s.message}>{message}</p>
            </div>

            <div className={s.body}>
                <div className={s.detailBox}>
                    {moduleLabel && (
                        <div className={s.detailRow}>
                            <span className={s.detailLabel}>Moduł</span>
                            <span className={s.detailValue}>{moduleLabel}</span>
                        </div>
                    )}
                    {reason && (
                        <div className={s.detailRow}>
                            <span className={s.detailLabel}>Powód</span>
                            <span className={s.detailValue}>{reason}</span>
                        </div>
                    )}
                    <div className={s.detailRow}>
                        <span className={s.detailLabel}>Adres</span>
                        <span className={`${s.detailValue} ${s.detailMono}`}>{location.pathname}</span>
                    </div>
                    {user?.email && (
                        <div className={s.detailRow}>
                            <span className={s.detailLabel}>Konto</span>
                            <span className={`${s.detailValue} ${s.detailMono}`}>{user.email}</span>
                        </div>
                    )}
                </div>

                {/* [polecenie właściciela 2026-09-06, pkt 1] TRZY KROKI I BLOK „KTO NADAJE DOSTĘP" ZDJĘTE.
                    „Za dużo informacji kto nadaje dostęp. Brak uprawnień to brak uprawnień,
                     i albo wniosek albo do widzenia."
                    Ekran mówi teraz trzy rzeczy: czego dotyczy, dlaczego nie wchodzisz i co możesz zrobić —
                    a to ostatnie jest przyciskami, nie akapitami. Wykład o tym, kto w firmie nadaje
                    uprawnienia, nie pomaga człowiekowi, który właśnie odbil się od drzwi. */}

                {sentNumber ? (
                    <div className={s.successBox}>
                        <FiCheck size={15} />
                        <span>
                            Zgłoszenie <strong>{sentNumber}</strong> trafiło do działu IT razem z Twoim
                            uzasadnieniem. Odpowiedź dostaniesz w powiadomieniach i na e-mail.
                        </span>
                    </div>
                ) : mozliwyWniosek ? (
                    <div className={s.helpRow}>
                        <FiLifeBuoy size={14} className={s.helpIcon} />
                        <span>
                            {/* [polecenie właściciela 2026-09-08] BYłO: „w szufladzie dopiszesz uzasadnienie".
                                „Szuflada" to nazwa ELEMENTU INTERFEJSU, która pracownikowi nic nie mówi —
                                a zdanie milczało o rzeczy najważniejszej: że to jest ZGŁOSZENIE, które przejdzie
                                weryfikację i może zostać odrzucone. */}
                            To otwiera zgłoszenie o dostęp w zakresie tego uprawnienia. Napiszesz w nim, do czego
                            jest Ci potrzebny; dane techniczne z tego ekranu dołączymy sami. Zgłoszenie zostanie
                            zweryfikowane i obsłużone albo odrzucone — o wyniku poinformujemy Cię.
                        </span>
                    </div>
                ) : (
                    <div className={s.helpRow}>
                        <FiLifeBuoy size={14} className={s.helpIcon} />
                        <span>
                            Tu nie brakuje uprawnienia — moduł jest wyłączony w całej aplikacji, więc dział IT
                            nie ma czego nadać. O ponownym włączeniu decyduje administrator aplikacji.
                        </span>
                    </div>
                )}

                {/* [polecenie właściciela 2026-09-06, pkt 1] „źe jeśli administrator właśnie go nadał,
                    wyloguj się i zaloguj ponownie — to jest błędna informacja."
                    I to prawda: `Odśwież uprawnienia` uderza w GET /users/me, który PRZELICZA uprawnienia
                    od zera i wydaje nowy token (UsersController: CalculateEffectivePermissions +
                    GenerateAccessToken). Zdanie o wylogowaniu kazało więc robić ręcznie to, co przycisk
                    obok właśnie zrobil. Zostaje sam fakt. */}
                {refreshedNoChange && (
                    <div className={s.helpRow}>
                        <FiRefreshCw size={14} className={s.helpIcon} />
                        <span>Sprawdziliśmy uprawnienia na serwerze — nadal nie ma tu dostępu.</span>
                    </div>
                )}

                {sendError && !drawerOpen && <div className={s.errorBox}>{sendError}</div>}
            </div>

            {/* [KANON OKIEN 2026-09-07] Stopka wg kanonu: przyciski PO PRAWEJ, akcja główna na końcu.
                Było: rozpychająca przekładka `spacer`, która wypychała „Wróć" na lewą krawędź, a resztę
                zostawiała po lewej — układ, którego kanon okien w ogóle nie zna, i który sprawiał, że
                to okno kończyło się inaczej niż każde inne w aplikacji. */}
            <div className={s.actions}>
                <button type="button" className={s.ghostBtn} onClick={handleCopy}>
                    {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                    {copied ? "Skopiowano" : "Kopiuj komunikat"}
                </button>

                <button
                    type="button"
                    className={s.ghostBtn}
                    onClick={handleRefreshPermissions}
                    disabled={refreshing}
                    title="Pobierz aktualne uprawnienia z serwera (bez wylogowania)"
                >
                    <FiRefreshCw size={14} />
                    {refreshing ? "Sprawdzam…" : "Odśwież uprawnienia"}
                </button>

                {asModal ? (
                    <button type="button" className={s.ghostBtn} onClick={goBack}>
                        <FiArrowLeft size={14} /> Wróć
                    </button>
                ) : (
                    <button
                        type="button"
                        className={s.ghostBtn}
                        onClick={() => navigate(localizedPath("dashboard"))}
                    >
                        <FiHome size={14} /> Przejdź na pulpit
                    </button>
                )}

                {mozliwyWniosek && !sentNumber && (
                    <button
                        type="button"
                        className={s.primaryBtn}
                        onClick={openDrawer}
                        disabled={sending}
                    >
                        <FiSend size={14} />
                        Wyślij zgłoszenie do IT
                    </button>
                )}
            </div>
        </motion.div>
    );

    if (!asModal) {
        return (
            <>
                <div className={s.pageWrap}>{card}</div>
                {drawer}
            </>
        );
    }

    // Modal: tło NIE zamyka okna (konwencja repo) — klik w tło potrząsa kartą.
    return (
        <>
            <div className={s.overlay} onClick={shakeModal}>
                {card}
            </div>
            {drawer}
        </>
    );
};

export default AccessDeniedScreen;
